import {HModal} from "@client/components/Modal";
import * as dialogs from "@client/features/dialogs";
import * as typography from "@client/shared/typography";
import {RadioGroup} from "@headlessui/react";
import {classNames, server} from "@reactivated";
import React from "react";
import ReactMarkdown from "react-markdown";

interface Props {
    template: server.manager.templates.ProgramTemplate.output;
    onSelect: (activeProgram: server.journal.workouts.ActiveProgram.output) => void;
    target: {
        user_id: number;
    };
}

export function ProgramCard(props: Props) {
    const {template} = props;
    const previewDialog = dialogs.useDialog({
        modal: HModal,
        Content: () => (
            <HModal.Content
                title={<h2 className="text-lg font-semibold">{template.name}</h2>}
                content={
                    <div className="space-y-4 text-base!">
                        <ReactMarkdown>{template.description}</ReactMarkdown>
                    </div>
                }
                actions={
                    <typography.AsyncButton
                        type="button"
                        className="w-full rounded-sm bg-indigo-600 px-2 py-1 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
                        onClick={async () => {
                            await server.journal.workouts.select_program_template({
                                source_template: template.uuid,
                                user_id: props.target.user_id,
                                should_sync: true,
                                exercise_maxes: [],
                            });
                            const result = await server.journal.workouts.workouts();

                            if (result.type == "success") {
                                props.onSelect(result.data.active_program!);
                            }
                        }}
                    >
                        Start
                    </typography.AsyncButton>
                }
            />
        ),
    });

    return (
        <li
            key={template.id}
            className="relative col-span-1 rounded-lg border border-stone-200 bg-white shadow-xs"
        >
            {previewDialog.dialog}
            <button className="absolute inset-0" onClick={previewDialog} />
            <div className="flex w-full items-center justify-between space-x-6 p-6">
                <div className="flex-1 truncate">
                    <div className="flex items-center space-x-3">
                        <h3 className="truncate text-sm font-medium text-gray-900">
                            {template.name}
                        </h3>
                    </div>
                    <p className="mt-1 truncate text-sm text-gray-500">
                        {template.workout_groups.map((g) => g.workouts).flat().length ==
                        1
                            ? "1 workout"
                            : `${
                                  template.workout_groups.map((g) => g.workouts).flat()
                                      .length
                              } workouts`}

                        {([].length == 0 ||
                            template.user_id != props.target.user_id) && (
                            <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-sky-600/20 ring-inset">
                                Program
                            </span>
                        )}
                    </p>
                </div>
            </div>
        </li>
    );
}

const getProgramDescription = (
    template: server.manager.templates.ProgramTemplate.output,
) =>
    template.workout_groups.map((g) => g.workouts).flat().length == 1
        ? "1 workout"
        : `${template.workout_groups.map((g) => g.workouts).flat().length} workouts`;

function ProgramInfo({
    template,
}: {
    template: server.manager.templates.ProgramTemplate.output;
}) {
    const previewDialog = dialogs.useDialog({
        modal: HModal,
        Content: ({close}) => (
            <HModal.Content
                title={<h2 className="text-lg font-semibold">{template.name}</h2>}
                content={
                    <div className="space-y-4 text-base!">
                        <ReactMarkdown>{template.description}</ReactMarkdown>
                    </div>
                }
                actions={
                    <button
                        type="button"
                        className="w-full rounded-sm bg-indigo-600 px-2 py-1 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 focus-visible:outline-solid"
                        onClick={close}
                    >
                        Close
                    </button>
                }
            />
        ),
    });

    return (
        <>
            {previewDialog.dialog}
            <button
                type="button"
                className="rounded-md bg-indigo-50 px-2.5 py-1.5 text-sm font-semibold text-indigo-600 shadow-xs hover:bg-indigo-100"
                onClick={(event) => {
                    event.preventDefault();
                    void previewDialog();
                }}
            >
                Info
            </button>
        </>
    );
}

export function SelectProgramTemplate({
    selected,
    setSelected,
    programs,
}: {
    selected: UUID | null;
    setSelected: (value: UUID | null) => void;
    programs: server.manager.templates.ProgramTemplate.output[];
}) {
    return (
        <fieldset aria-label="Server size">
            <RadioGroup
                value={selected}
                onChange={(value) => {
                    setSelected(value);
                }}
                className="space-y-4"
            >
                {programs.map((program) => (
                    <RadioGroup.Option
                        key={program.uuid}
                        value={program.uuid}
                        aria-label={program.name ?? ""}
                        aria-description={getProgramDescription(program)}
                        className={() =>
                            classNames(
                                "relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-xs focus:outline-hidden sm:flex sm:justify-between",
                            )
                        }
                    >
                        {({checked}) => (
                            <>
                                <span className="flex items-center">
                                    <span className="flex flex-col text-sm">
                                        <span className="font-medium text-gray-900">
                                            {program.name}
                                        </span>
                                        <span className="text-gray-500">
                                            {getProgramDescription(program)}
                                        </span>
                                    </span>
                                </span>
                                <span className="flex items-center">
                                    <ProgramInfo template={program} />
                                </span>
                                {/*
                                <span className="hidden mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                                    <span className="font-medium text-gray-900">
                                        $80
                                    </span>
                                    <span className="ml-1 text-gray-500 sm:ml-0">
                                        /mo
                                    </span>
                                </span>
                                */}

                                <span
                                    className={classNames(
                                        checked
                                            ? "border-indigo-600"
                                            : "border-transparent",
                                        "border-2 pointer-events-none absolute -inset-px rounded-lg",
                                    )}
                                    aria-hidden="true"
                                />
                            </>
                        )}
                    </RadioGroup.Option>
                ))}
            </RadioGroup>
        </fieldset>
    );
}
